import React, { useState, useRef } from "react";
import { X, FileText, Upload, Save, Loader2, CheckCircle2, AlertCircle, FileUp } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { useAuth } from "../../../../context/AuthContext";
import Swal from "sweetalert2";
import { 
  insertarInformacionGeneral, 
  insertarArchivoInformacionGeneral 
} from "../../../../api/informacionGeneralService";

interface CreateInformacionGeneralModalProps {
  isOpen: boolean;
  onClose: (created: boolean) => void;
}

export default function CreateInformacionGeneralModal({
  isOpen,
  onClose
}: CreateInformacionGeneralModalProps) {
  const { session } = useAuth();
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_CHARS = 100;

  const handleSave = async () => {
    if (!descripcion.trim()) {
      Swal.fire("Atención", "La descripción es obligatoria.", "warning");
      return;
    }

    if (descripcion.length > MAX_CHARS) {
       Swal.fire("Límite excedido", `La descripción no puede superar los ${MAX_CHARS} caracteres.`, "warning");
       return;
    }

    try {
      setIsSaving(true);
      const unidadCodigo = session?.unidad?.id || "";
      
      // Paso 1: Insertar registro base
      const response = await insertarInformacionGeneral({
        unidad_residencial_codigo: unidadCodigo,
        titulo: titulo,
        descripcion: descripcion
      });

      if (response.code === "1" && response.object_ && response.object_.length > 0) {
        const newRecord = response.object_[0];
        const newCodigo = newRecord.ige_codigo;

        // Paso 2: Si hay archivo, subirlo
        if (selectedFile) {
          const fileResponse = await insertarArchivoInformacionGeneral({
            iga_informacion_general: newCodigo.toString(),
            iga_archivo: selectedFile
          });

          if (fileResponse.code !== "1") {
            console.error("Error al subir archivo, pero el registro se creó:", fileResponse.message);
            // Podríamos avisar al usuario que el registro se creó pero el archivo no
          }
        }

        await Swal.fire({
          icon: "success",
          title: "¡Creado exitosamente!",
          text: "La nueva información ha sido publicada.",
          timer: 2000,
          showConfirmButton: false,
          confirmButtonColor: "#3d5a40",
        });
        onClose(true);
      } else {
        Swal.fire("Error", response.message || "No se pudo crear el registro.", "error");
      }
    } catch (error) {
      console.error("Error creating information:", error);
      Swal.fire("Error", "Error de conexión al intentar crear el registro.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col border border-gray-100 animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="bg-military-600 p-4 flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <Plus size={20} className="text-white" />
            </div>
            <div>
              <h3 className="font-bold text-lg leading-tight">Nueva Información</h3>
              <p className="text-xs text-white/70">Publicar comunicado para residentes</p>
            </div>
          </div>
          <button 
            onClick={() => onClose(false)}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {/* Input Title */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1">
              Título
            </label>
            <input
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              className="w-full p-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-military-500 focus:border-military-500 transition-all text-sm bg-gray-50/50"
              placeholder="Ej: Mantenimiento de Ascensores"
            />
          </div>

          {/* Input Description */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1">
                Descripción <span className="text-red-500">*</span>
              </label>
              <span className={`text-[10px] font-bold ${descripcion.length > MAX_CHARS ? 'text-red-500' : 'text-gray-400'}`}>
                {descripcion.length} / {MAX_CHARS}
              </span>
            </div>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              className="w-full min-h-[100px] p-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-military-500 focus:border-military-500 transition-all text-sm resize-none bg-gray-50/50"
              placeholder="Escribe el mensaje aquí (máximo 100 caracteres)..."
              maxLength={MAX_CHARS + 10}
            />
          </div>

          {/* File Selector */}
          <div className="space-y-3">
            <label className="text-xs font-bold text-gray-500 uppercase">Adjuntar Archivo (Opcional)</label>
            <div 
              className={`border-2 border-dashed rounded-xl p-6 transition-all flex flex-col items-center justify-center gap-3 cursor-pointer
                ${selectedFile ? 'border-military-400 bg-military-50' : 'border-gray-200 bg-gray-50 hover:border-military-300'}`}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                onChange={handleFileChange}
                accept=".pdf,.jpg,.jpeg,.png"
              />
              {selectedFile ? (
                <>
                  <div className="bg-military-500 text-white p-3 rounded-full">
                    <CheckCircle2 size={24} />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-bold text-military-700 truncate max-w-[200px]">
                      {selectedFile.name}
                    </p>
                    <p className="text-[10px] text-military-500 uppercase font-medium">Archivo seleccionado</p>
                  </div>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                    className="text-xs text-red-500 hover:underline font-bold"
                  >
                    Quitar archivo
                  </button>
                </>
              ) : (
                <>
                  <div className="bg-gray-200 text-gray-400 p-3 rounded-full">
                    <FileUp size={24} />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-bold text-gray-600">Haz clic para buscar</p>
                    <p className="text-[10px] text-gray-400 uppercase font-medium">PDF, JPG o PNG</p>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="p-3 bg-blue-50 rounded-lg flex items-start gap-2 border border-blue-100">
            <AlertCircle size={14} className="text-blue-500 mt-0.5 shrink-0" />
            <p className="text-[10px] text-blue-700 leading-tight">
              Al guardar, la información será visible inmediatamente para los residentes.
            </p>
          </div>
        </div>

        {/* Action Button */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 mt-auto">
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full bg-military-600 hover:bg-military-700 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-3 shadow-lg shadow-military-100 transition-all active:scale-95"
          >
            {isSaving ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                <span>GUARDANDO...</span>
              </>
            ) : (
              <>
                <Save size={20} />
                <span>PUBLICAR AHORA</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

const Plus = ({ size, className }: { size: number, className?: string }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);
